import React from 'react';
import styled from 'styled-components';

import GreenCheckMarkBadge from 'src/assets/green-check-mark-badge';
import useUI from 'src/hooks/use-ui';
import { AccordionBanner } from 'src/payments/components/accordion-banner/accordion-banner';
import { CashBackDetails, CashBackTermsLink, bankStatementTerms } from 'src/payments/components/cash-back-details';
import { useCashBackSurfaceGate } from 'src/payments/hooks/use-cash-back-surface-gate';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { Typography } from 'src/typography';
import { Button } from 'src/components/core';

const EXPOSURE_SOURCE = 'product_description';

const BankLogosMarqueeVertical = React.lazy(() =>
  import('src/payments/components/bank-logos-marquee-vertical').then((module) => ({
    default: module.BankLogosMarqueeVertical,
  }))
);

// The CTA lives inside the accordion summary's mainContent; the summary itself is the disclosure
// control, so every interaction path (click, Space, Enter) must stop propagation to avoid
// toggling the accordion when the CTA is used.
const stopSummaryToggle = (event: React.SyntheticEvent): void => event.stopPropagation();

export const PdpCashBackAccordion = (): JSX.Element | null => {
  const { showCashBack, cashBackValue } = useCashBackSurfaceGate('productDescription');
  const { isEnrolledDutchiePay } = useDutchiePay();
  const { userType } = useIncentives();
  const UI = useUI();
  const { openDutchiePay } = useOpenDutchiePay({
    enrollmentSource: `pdp-cash-back-${UI?.variant}`,
  });

  useIncentiveExposure({
    incentiveValue: showCashBack && !UI?.isKiosk ? cashBackValue : undefined,
    userType,
    isCashBack: true,
    source: EXPOSURE_SOURCE,
  });

  // No signup at kiosk, so the surface suppresses there regardless of flag/eligibility.
  if (UI?.isKiosk || !showCashBack || cashBackValue === undefined) {
    return null;
  }

  const value = cashBackValue;

  const mainContent = isEnrolledDutchiePay ? (
    <HeadlineText>
      <strong>Bank linked</strong>
      <br />
      Pay by Bank at checkout to claim ${value} back. <u>Terms Apply.</u>
    </HeadlineText>
  ) : (
    <HeadlineRow>
      <HeadlineText>
        <strong>${value} cash back</strong> on your first order paid by bank. <u>Terms Apply.</u>
      </HeadlineText>
      <LinkBankButton
        type='button'
        onClick={(event) => {
          stopSummaryToggle(event);
          openDutchiePay();
        }}
        onKeyDown={stopSummaryToggle}
        onKeyUp={stopSummaryToggle}
      >
        Link bank
      </LinkBankButton>
    </HeadlineRow>
  );

  const toggleContent = isEnrolledDutchiePay ? (
    <Typography.Label size='small'>
      Choose Pay now at checkout &amp; complete your Pay by Bank payment to claim ${value} cash back.{' '}
      {bankStatementTerms(value)}
      <br />
      <CashBackTermsLink>View full terms</CashBackTermsLink>
    </Typography.Label>
  ) : (
    <CashBackDetails value={value} />
  );

  return (
    <StyledAccordionBanner
      marginBottom='16px'
      borderRadius='var(--dcm-pdp-cash-back-radius, 4px)'
      graphic={
        isEnrolledDutchiePay ? (
          <GreenCheckMarkBadge />
        ) : (
          <React.Suspense fallback={<BankLogoStripPlaceholder />}>
            <BankLogosMarqueeVertical />
          </React.Suspense>
        )
      }
      graphicWidth={isEnrolledDutchiePay ? undefined : '20px'}
      graphicFullHeight={!isEnrolledDutchiePay}
      mainContent={mainContent}
      toggleContent={toggleContent}
    />
  );
};

const StyledAccordionBanner = styled(AccordionBanner)`
  &.MuiPaper-root {
    height: unset;
    max-width: unset;
    min-height: unset;
    border: 1px solid var(--dcm-pdp-cash-back-border-color, ${({ theme }) => theme.colors.green20});
    background-color: var(--dcm-pdp-cash-back-background-color, #f4f8f7);
  }

  & > .MuiAccordionSummary-root,
  .MuiAccordionSummary-root.Mui-expanded {
    height: unset;
    min-height: unset;

    & > .MuiAccordionSummary-content {
      margin: 0;
    }
  }

  & > .MuiCollapse-container {
    border-top: 1px solid var(--dcm-pdp-cash-back-border-color, ${({ theme }) => theme.colors.green20});
  }
`;

const HeadlineRow = styled.span`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spaces[3]};
  justify-content: space-between;
  width: 100%;
`;

const BankLogoStripPlaceholder = styled.div`
  width: 100%;
  height: 100%;
`;

const HeadlineText = styled.span`
  color: var(--dcm-pdp-cash-back-text-color, ${({ theme }) => theme.colors.black});

  u {
    text-decoration-color: var(--dcm-pdp-cash-back-accent, ${({ theme }) => theme.colors.green90});
  }
`;

const LinkBankButton = styled(Button)`
  background-color: var(--dcm-pdp-cash-back-cta-background, #006149);
  border-radius: var(--dcm-pdp-cash-back-cta-radius, 6px);
  font-weight: 600;
  padding: 4px 6px;
  text-transform: var(--dcm-pdp-cash-back-cta-text-transform, none);
`;
